import { useState } from 'react'

import classNames from 'classnames'
import { Check, ChevronUp } from 'lucide-react'

import { TournamentListCard, type TournamentListCardData } from 'components/TournamentListCard'

import { FOCUS_RING } from './ui'
import { STEPS } from './wizard'

export type PreviewSummary = { label: string; value: string; muted?: boolean }[]

const ReadyList = ({ complete }: { complete: boolean[] }) => (
  <ul className="grid gap-1.5" aria-label="Step completeness">
    {STEPS.slice(0, -1).map((step, index) => (
      <li key={step.id} className="flex items-center gap-2 text-xs">
        <span
          aria-hidden
          className={classNames(
            'flex h-4 w-4 items-center justify-center rounded-full',
            complete[index] ? 'bg-green/15 text-green' : 'border border-light-border-primary dark:border-dark-border-primary',
          )}
        >
          {complete[index] && <Check size={10} strokeWidth={3} />}
        </span>
        <span className={complete[index] ? 'text-light-typo-primary dark:text-dark-typo-primary' : 'text-light-typo-gray dark:text-dark-typo-gray'}>
          {step.label}
          <span className="sr-only">{complete[index] ? ': ready' : ': needs attention'}</span>
        </span>
      </li>
    ))}
  </ul>
)

const SummaryList = ({ summary }: { summary: PreviewSummary }) => (
  <dl className="divide-y divide-light-border-primary/60 dark:divide-dark-border-primary/60">
    {summary.map((row) => (
      <div key={row.label} className="flex items-baseline justify-between gap-4 py-2 text-sm">
        <dt className="text-light-typo-gray dark:text-dark-typo-gray">{row.label}</dt>
        <dd
          className={classNames(
            'min-w-0 truncate text-right font-display text-base font-semibold',
            row.muted ? 'text-light-typo-gray dark:text-dark-typo-gray' : 'text-light-typo-primary dark:text-dark-typo-primary',
          )}
        >
          {row.value}
        </dd>
      </div>
    ))}
  </dl>
)

const PreviewCard = ({ card }: { card: TournamentListCardData }) => (
  // The real /tournaments card, made inert: it's a picture of the result, not a link.
  <div aria-hidden className="pointer-events-none select-none">
    <TournamentListCard tournament={card} status="upcoming" isGridView />
  </div>
)

/** Desktop: sticky right column. */
export const LivePreview = ({ card, summary, complete }: { card: TournamentListCardData; summary: PreviewSummary; complete: boolean[] }) => (
  <div className="grid gap-5">
    <div>
      <p className="mb-2 text-xs font-semibold text-light-typo-gray dark:text-dark-typo-gray">How it will look on Tournaments</p>
      <PreviewCard card={card} />
    </div>
    <SummaryList summary={summary} />
    <div>
      <p className="mb-2 text-xs font-semibold text-light-typo-gray dark:text-dark-typo-gray">Ready to create</p>
      <ReadyList complete={complete} />
    </div>
  </div>
)

/** Mobile / tablet: a summary bar above the footer that expands into the preview. */
export const MobilePreviewBar = ({ card, summary, complete }: { card: TournamentListCardData; summary: PreviewSummary; complete: boolean[] }) => {
  const [open, setOpen] = useState(false)
  const done = complete.slice(0, -1).filter(Boolean).length
  const total = STEPS.length - 1
  const headline = summary.filter((r) => !r.muted && r.value !== '—').slice(0, 2).map((r) => r.value).join(' · ')

  return (
    <div className="border-t border-light-border-primary dark:border-dark-border-primary lg:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="ct-mobile-preview"
        className={classNames('flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left sm:px-6', FOCUS_RING)}
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-light-typo-gray dark:text-dark-typo-gray">
            Preview · {done} of {total} steps ready
          </span>
          <span className="block truncate font-display text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">{headline}</span>
        </span>
        <ChevronUp
          size={18}
          aria-hidden
          className={classNames('flex-shrink-0 text-light-typo-gray dark:text-dark-typo-gray transition-transform motion-reduce:transition-none', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div id="ct-mobile-preview" className="max-h-[45vh] overflow-y-auto px-4 pb-4 sm:px-6 motion-safe:animate-step-in">
          <div className="mx-auto grid max-w-sm gap-4">
            <PreviewCard card={card} />
            <SummaryList summary={summary} />
            <ReadyList complete={complete} />
          </div>
        </div>
      )}
    </div>
  )
}
